import React, { Children, createContext, Fragment, useContext } from 'react';
import { stateKeys } from './model';
import { getReplacedString } from '../../helpers/getReplacedString';

const getOptionsFromOptgroupChildren = (children) =>
  Children.toArray(children).reduce((acc, { props, key }) => {
    if ('value' in props) {
      acc.push({ ...props, key });
    }

    return acc;
  }, []);

export const getOptionsFromChildren = (children) =>
  Children.toArray(children).reduce((acc, { props, key }) => {
    if ('label' in props) {
      const options =
        props.options || getOptionsFromOptgroupChildren(props.children);

      acc.push({ options, label: props.label, key });
    }

    if ('value' in props) {
      acc.push({ ...props, key });
    }

    return acc;
  }, []);

const getOptionText = (option) => {
  if (typeof option.content === 'string') {
    return option.content;
  }

  if (typeof option.children === 'string') {
    return option.children;
  }

  if (option.text) {
    return option.text;
  }

  return option.value;
};

const isEmpty = (value) => {
  if (value === null || value === undefined) {
    return true;
  }

  if (typeof value === 'object') {
    return Array.isArray(value)
      ? value.length === 0
      : Object.keys(value).length === 0;
  }

  return false;
};

export const getSelectedOptionsContent = ({
  options,
  value,
  renderSelectedOption,
}) => {
  if (isEmpty(value)) {
    return null;
  }

  const val = Array.isArray(value) ? value : [value];

  const selectedOptions = val.reduce((acc, val) => {
    const selectedOption = options.find((opt) => opt.value === val);
    acc.push(selectedOption || { value: val });
    return acc;
  }, []);

  return renderSelectedOption
    ? selectedOptions.map((opt) => (
        <Fragment key={opt.value}>{renderSelectedOption(opt)}</Fragment>
      ))
    : selectedOptions.map((opt) => getOptionText(opt)).join(', ');
};

const getOptionSearch = (option) => {
  if (option.search) {
    return option.search;
  }

  if (typeof option.children === 'string') {
    return option.children;
  }

  return '';
};

export const getFlattenOptionsAndCount = (options) => {
  if (!options?.length) {
    return {
      [stateKeys.options]: [],
      [stateKeys.optionsCount]: 0,
    };
  }

  let count = 0;

  const flattenOptions = options.reduce((acc, option) => {
    if ('label' in option) {
      acc.push({ label: option.label, clickable: false, data: option.data });
      acc.push(...(option.options || []));
      count += option.options?.length || 0;
    } else {
      count += 1;
      acc.push(option);
    }

    return acc;
  }, []);

  return {
    [stateKeys.options]: flattenOptions,
    [stateKeys.optionsCount]: count,
  };
};

export const isFn = (fn) => typeof fn === 'function';

export const createContextWithHook = () => {
  const Context = createContext();
  return { Context, useContext: () => useContext(Context) };
};

export const filterOptions = ({ options, search, createSearch, value }) => {
  const trimmedSearch = search?.trim() ?? '';

  let filteredOptions = options;

  if (trimmedSearch) {
    const searchCreator = isFn(createSearch) ? createSearch : getOptionSearch;

    const searchVal = getReplacedString(trimmedSearch);

    filteredOptions = options.filter((opt) => {
      const target = getReplacedString(searchCreator(opt));
      return target.includes(searchVal);
    });
  }

  if (!!value && Array.isArray(value) && value.length > 0) {
    const valueSet = new Set(value);

    const matchedOptions = [];
    const otherOptions = [];

    filteredOptions.forEach((opt) => {
      const optValue = opt?.value ?? opt;
      if (valueSet.has(optValue)) {
        matchedOptions.push(opt);
      } else {
        otherOptions.push(opt);
      }
    });

    filteredOptions = [...matchedOptions, ...otherOptions];
  }

  return filteredOptions;
};
